import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Clock, CheckCircle, XCircle, Truck, Search, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { Order } from '../types'
import { getCache, getCacheStale, setCache } from '../lib/cache'
import { showOfflineToast } from '../lib/toastManager'

type OrderStatus = 'pending' | 'preparing' | 'ready' | 'delivering' | 'delivered' | 'cancelled'

const OrderHistory = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const [allOrders, setAllOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<OrderStatus | 'all'>('all')

  // Ref para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingOrdersRef = useRef(false)

  // Group orders by status
  const ordersByStatus = {
    all: allOrders,
    pending: allOrders.filter(o => o.status === 'pending'),
    preparing: allOrders.filter(o => o.status === 'preparing'),
    ready: allOrders.filter(o => o.status === 'ready'),
    delivering: allOrders.filter(o => o.status === 'delivering'),
    delivered: allOrders.filter(o => o.status === 'delivered'),
    cancelled: allOrders.filter(o => o.status === 'cancelled')
  }

  useEffect(() => {
    if (!isAuthenticated || !user) return
    
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingOrdersRef.current) return
    loadingOrdersRef.current = true

    loadOrders().finally(() => {
      loadingOrdersRef.current = false
    })
  }, [isAuthenticated, user])

  const loadOrders = async () => {
    if (!user) return
    
    const cacheKey = `orders_history_${user.id}`
    
    // Intentar cargar desde cache primero
    const cachedOrders = getCacheStale<Order[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      setAllOrders(cachedOrders)
      setLoading(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }
    
    try {
      setLoading(true)
      const response = await api.get('/orders', { params: { limit: 100 } })
      if (response.data.success) {
        // Normalizar los datos de las órdenes
        const normalizedOrders = (response.data.data || []).map((order: any) => ({
          ...order,
          totalAmount: Number(order.total_amount || order.totalAmount) || 0,
          deliveryFee: Number(order.delivery_fee || order.deliveryFee) || 0,
          taxAmount: Number(order.tax_amount || order.taxAmount) || 0,
          estimatedDeliveryTime: Number(order.estimated_delivery_time || order.estimatedDeliveryTime) || 0,
          createdAt: order.created_at || order.createdAt,
          customerName: order.customer_name || order.customerName,
          customerPhone: order.customer_phone || order.customerPhone,
          paymentMethod: order.payment_method || order.paymentMethod,
          paymentStatus: order.payment_status || order.paymentStatus,
          items: order.items || []
        }))
        setAllOrders(normalizedOrders)
        
        // Guardar en cache (5 minutos de TTL - las órdenes cambian frecuentemente)
        setCache(cacheKey, normalizedOrders, 5 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading orders:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar pedidos')
      
      // Solo borrar órdenes si no hay datos en cache
      if (!cachedOrders || cachedOrders.length === 0) {
        setAllOrders([])
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadOrders()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border-yellow-300'
      case 'preparing':
        return 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border-blue-300'
      case 'ready':
        return 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 border-purple-300'
      case 'delivering':
        return 'bg-gradient-to-r from-orange-100 to-orange-50 text-orange-800 border-orange-300'
      case 'delivered':
        return 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border-green-300'
      case 'cancelled':
        return 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border-red-300'
      default:
        return 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border-gray-300'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente'
      case 'preparing':
        return 'Preparando'
      case 'ready':
        return 'Listo'
      case 'delivering':
        return 'En Camino'
      case 'delivered':
        return 'Entregado'
      case 'cancelled':
        return 'Cancelado'
      default:
        return status
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'preparing':
        return <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
      case 'ready':
        return <Package className="w-4 h-4 sm:w-5 sm:h-5" />
      case 'delivering':
        return <Truck className="w-4 h-4 sm:w-5 sm:h-5" />
      case 'delivered':
        return <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
      case 'cancelled':
        return <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
      default:
        return <Package className="w-4 h-4 sm:w-5 sm:h-5" />
    }
  }

  const filteredOrders = ordersByStatus[activeTab].filter(order => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        order.id.toLowerCase().includes(search) ||
        order.customerName?.toLowerCase().includes(search) ||
        order.store?.name?.toLowerCase().includes(search) ||
        false
      )
    }
    return true
  })

  const tabs: Array<{ value: OrderStatus | 'all', label: string, count: number }> = [
    { value: 'all', label: 'Todos', count: allOrders.length },
    { value: 'pending', label: 'Pendientes', count: ordersByStatus.pending.length },
    { value: 'preparing', label: 'Preparando', count: ordersByStatus.preparing.length },
    { value: 'ready', label: 'Listos', count: ordersByStatus.ready.length },
    { value: 'delivering', label: 'En Camino', count: ordersByStatus.delivering.length },
    { value: 'delivered', label: 'Entregados', count: ordersByStatus.delivered.length },
    { value: 'cancelled', label: 'Cancelados', count: ordersByStatus.cancelled.length }
  ]

  if (!isAuthenticated) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center pb-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
          <p className="mt-4 sm:mt-6 text-sm sm:text-base text-gray-600 font-semibold">Cargando pedidos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6 lg:py-8">
        {/* Header - iMac Style */}
        <div className="bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6 mb-4 sm:mb-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 tracking-tight">Mis Pedidos</h1>
              <p className="text-xs sm:text-base text-gray-600 font-medium">Historial de todos tus pedidos</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2.5 sm:p-3.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95 shadow-sm hover:shadow-md"
            >
              <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 text-gray-700 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search Bar - iMac Style */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-3 sm:p-5 mb-4 sm:mb-6">
          <div className="relative">
            <Search className="absolute left-3 sm:left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-6 sm:h-6" />
            <input
              type="text"
              placeholder="Buscar por ID, nombre o negocio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 sm:pl-14 pr-4 py-2.5 sm:py-4 border-2 border-gray-200 rounded-2xl sm:rounded-3xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-white text-sm sm:text-base shadow-sm hover:shadow-md"
            />
          </div>
        </div>

        {/* Tabs - iMac Style */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-2 sm:p-3 mb-4 sm:mb-6 overflow-x-auto">
          <div className="flex gap-2 sm:gap-3 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold text-xs sm:text-sm transition-all duration-300 active:scale-95 whitespace-nowrap ${
                  activeTab === tab.value
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-xl border-2 border-primary-500/50'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-transparent hover:border-gray-200'
                }`}
              >
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold ${
                    activeTab === tab.value
                      ? 'bg-white/30 text-white backdrop-blur-sm'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border-2 border-gray-200/60 p-6 sm:p-12 lg:p-16 text-center">
            <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center shadow-lg">
              <Package className="w-8 h-8 sm:w-12 sm:h-12 text-primary-500" />
            </div>
            <h3 className="text-base sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
              {activeTab === 'all' ? 'No tienes pedidos' : `No hay pedidos ${tabs.find(t => t.value === activeTab)?.label.toLowerCase()}`}
            </h3>
            <p className="text-xs sm:text-base text-gray-600 mb-6 sm:mb-8 font-medium">
              {activeTab === 'all' 
                ? 'Comienza a hacer pedidos en tus negocios favoritos'
                : 'Los pedidos con este estado aparecerán aquí'}
            </p>
            {activeTab === 'all' && (
              <button
                onClick={() => navigate('/stores')}
                className="px-6 sm:px-8 py-2.5 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl sm:rounded-2xl hover:from-primary-700 hover:to-primary-800 font-bold text-sm sm:text-base shadow-xl hover:shadow-2xl transition-all duration-300 active:scale-95"
              >
                Explorar Negocios
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer active:scale-[0.97] overflow-hidden group"
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                <div className="p-4 sm:p-5 lg:p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 flex-wrap">
                        <span className="text-[10px] sm:text-sm font-mono text-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 px-2 sm:px-3 py-1 rounded-lg border border-gray-200 font-semibold">
                          #{order.id.slice(0, 8)}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-sm font-bold border-2 shadow-sm ${getStatusColor(order.status)}`}>
                          {getStatusIcon(order.status)}
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm sm:text-lg lg:text-xl mb-1 sm:mb-2 group-hover:text-primary-600 transition-colors">
                        {order.store?.name || 'Negocio'}
                      </h3>
                      <p className="text-xs sm:text-base text-gray-600 mb-2 font-medium">
                        {order.items?.length || 0} producto(s) • RD${(Number(order.totalAmount) || 0).toFixed(2)}
                      </p>
                      <p className="text-[10px] sm:text-sm text-gray-500 font-medium">
                        {new Date(order.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 sm:gap-3">
                      <span className="text-base sm:text-xl lg:text-2xl font-bold text-primary-600">
                        RD${(Number(order.totalAmount) || 0).toFixed(2)}
                      </span>
                      {order.estimatedDeliveryTime && order.status !== 'delivered' && order.status !== 'cancelled' && (
                        <span className="text-[10px] sm:text-sm text-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 px-2 sm:px-3 py-1 rounded-lg border border-gray-200 font-semibold">
                          ~{order.estimatedDeliveryTime} min
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Order Items Preview */}
                  {order.items && order.items.length > 0 && (
                    <div className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t-2 border-gray-100">
                      <div className="flex flex-wrap gap-2">
                        {order.items.slice(0, 3).map((item: any, index: number) => (
                          <span
                            key={index}
                            className="text-[10px] sm:text-sm bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg font-semibold border border-primary-200/50"
                          >
                            {item.quantity}x {item.product?.name || 'Producto'}
                          </span>
                        ))}
                        {order.items.length > 3 && (
                          <span className="text-[10px] sm:text-sm text-gray-500 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-gray-50 rounded-lg border border-gray-200 font-medium">
                            +{order.items.length - 3} más
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default OrderHistory
